import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BRAND } from '@rydres/shared';
import { Card, PlacesEditor, Spinner, formatUsd } from '@rydres/ui';
import { api, geoApi } from '../lib/api.js';
import type { PassengerMe, PublicPage } from '../lib/types.js';
import { Shell, useRiderGate } from './Passenger.js';

/** One driver's services, each opening their booking page with it chosen. */
function DriverServices({ driver, page }: { driver: PassengerMe['drivers'][number]; page: PublicPage | null | undefined }) {
  const name = driver.displayName ?? driver.handle;
  return (
    <section className="space-y-2" aria-label={`${name}'s services`}>
      <h2 className="px-1 text-xs font-bold uppercase tracking-wide text-muted">{name}</h2>
      {page === undefined ? (
        <Spinner />
      ) : page === null ? (
        <p className="px-1 text-sm text-ink2">{name} is not taking bookings on {BRAND.name} right now.</p>
      ) : (
        <ul className="space-y-2">
          {page.services.map((s) => (
            <li key={s.id}>
              <Link to={`/${page.handle}?service=${s.id}`} className="flex items-center justify-between gap-3 rounded-2xl border border-line bg-card p-4">
                <div className="min-w-0">
                  <div className="font-bold">{s.name}</div>
                  {s.description && <div className="line-clamp-2 text-sm text-ink2">{s.description}</div>}
                  <div className="mt-1 text-xs text-muted">Up to {s.maxPassengers} passengers · per vehicle</div>
                </div>
                <div className="shrink-0 text-right">
                  <div className="text-lg font-black">{formatUsd(s.priceCents)}</div>
                  <div className="text-xs font-bold text-accent-text">Book</div>
                </div>
              </Link>
            </li>
          ))}
          <li>
            <Link to={`/${page.handle}?service=custom`} className="flex items-center justify-between gap-3 rounded-2xl border border-dashed border-line p-4">
              <div className="font-bold">Something else</div>
              <div className="text-xs font-bold text-accent-text">Ask for a price</div>
            </Link>
          </li>
        </ul>
      )}
    </section>
  );
}

/**
 * rydres.com/me/services: the services of the drivers the passenger rides with, to book with one
 * tap, and their saved places, the buttons that fill pickup and drop-off when they book.
 */
export function ServicesPage() {
  const { p, gate } = useRiderGate('Services', `Sign in with the mobile number you book with to see your ${BRAND.name} drivers' services and keep your places for quick booking.`);
  const [pages, setPages] = useState<Record<string, PublicPage | null>>({});
  const handles = p.me?.drivers.map((d) => d.handle).join(',') ?? '';
  useEffect(() => {
    if (!handles) return;
    for (const h of handles.split(','))
      api.call<PublicPage>('GET', `/public/pages/${encodeURIComponent(h)}`, { auth: false }).then(
        (page) => setPages((x) => ({ ...x, [h]: page })),
        () => setPages((x) => ({ ...x, [h]: null })),
      );
  }, [handles]);
  if (gate) return gate;
  const me = p.me!;
  return (
    <Shell>
      <h1 className="text-2xl font-black">Services</h1>
      {me.drivers.length === 0 ? (
        <p className="text-sm text-ink2">The services of drivers you book or share your details with show up here.</p>
      ) : (
        me.drivers.map((d) => <DriverServices key={d.handle} driver={d} page={pages[d.handle]} />)
      )}
      <Card title="Your places" info="One tap fills pickup or drop-off when you book. Your places are private, except your first home address, which drivers you share your details with see.">
        <PlacesEditor places={me.places} onSave={p.savePlaces} geo={geoApi} homeNote="Drivers you share your details with see this address." />
      </Card>
    </Shell>
  );
}
